import React, { useEffect, useState } from 'react';
import { FaInfoCircle, FaStar, FaTimes } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import api from '../../axios/axios'; // Adjust the path to your axios instance

// Review Modal Component
const ReviewModal = ({ isOpen, onClose, item, orderId, fetchOrderDetails }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.currentUser);

  const [reviewData, setReviewData] = useState({
    rating: 0,
    review: '',
  });

  const isEdit = item?.reviews?.length > 0;

  // Fetch review data when editing
  useEffect(() => {
    if (isEdit && item?.reviews?.length > 0) {
      const review = item.reviews[0]; // Assuming one review per user per item
      setReviewData({
        rating: review.rating,
        review: review.review,
      });
    } else {
      setReviewData({ rating: 0, review: '' }); // Reset for new review
    }
  }, [isEdit, item]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (reviewData.rating < 1 || !reviewData.review.trim()) {
      Swal.fire({
        title: 'Invalid Input',
        text: 'Please provide a rating (1-5) and a review comment.',
        icon: 'warning',
        confirmButtonText: 'Okay',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to submit your review?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, submit it!',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: isEdit ? 'Updating review...' : 'Submitting review...',
        text: 'Please wait while we process your review.',
        didOpen: () => Swal.showLoading(),
        allowOutsideClick: false,
      });

      try {
        const response = isEdit
          ? await api.put(
              'user/editReview',
              { rating: reviewData.rating, review: reviewData.review },
              { params: { userId: currentUser?._id, reviewId: item.reviews[0]._id } }
            )
          : await api.post( 
              'user/addReview',
              { rating: reviewData.rating, review: reviewData.review },
              { params: { userId: currentUser?._id, productId: item.productId._id } }
            );

        Swal.close();
        Swal.fire({
          title: isEdit ? 'Review Updated!' : 'Review Submitted!',
          text: isEdit ? 'Your review has been successfully updated.' : 'Your review has been successfully submitted.',
          icon: 'success',
          confirmButtonText: 'Great!',
        });
        fetchOrderDetails(orderId); // Refresh order details
        onClose();
      } catch (error) {
        console.log(error)
        Swal.close();
        Swal.fire({
          title: 'Error!',
          text: 'There was an error submitting your review. Please try again later.',
          icon: 'error',
          confirmButtonText: 'Okay',
        });
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-secondary">{isEdit ? 'Edit Review' : 'Add Review'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FaTimes size={20} />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Rating:</label>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  size={24}
                  className={star <= reviewData.rating ? 'text-yellow-400' : 'text-gray-300'}
                  onClick={() => setReviewData({ ...reviewData, rating: star })}
                  style={{ cursor: 'pointer' }}
                />
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Review:</label>
            <textarea
              value={reviewData.review}
              onChange={(e) => setReviewData({ ...reviewData, review: e.target.value })}
              className="w-full mt-1 p-2 border rounded-md"
              rows="4"
              placeholder="Write your review here..."
            />
          </div>
          <button
            onClick={handleSubmit}
            className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700"
          >
            {isEdit ? 'Update Review' : 'Submit Review'}
          </button>
        </div>
      </div>
    </div>
  );
};

const OrderItems = ({ items, openAttributeModal, openTaxModal, fetchOrderDetails, orderId }) => {
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleOpenReviewModal = (item) => {
    setSelectedItem(item);
    setIsReviewModalOpen(true);
  };

  return (
    <>
      <div className="space-y-6">
        {items.map((item) => {
          const attributeAdjustment = item.attributes.reduce((sum, attr) => sum + Number(attr.price || 0), 0);
          const taxAmount = item.taxes.reduce((sum, tax) => sum + Number(tax.taxAmount || 0), 0);
          const subtotalWithAttributes = Number(item.initialPrice) + attributeAdjustment;
          const hasReview = item.reviews && item.reviews.length > 0;

          return (
            <div key={item._id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
              <div className="p-4 md:p-6">
                <div className="flex flex-col md:flex-row">
                  <img
                    src={item.productId.images[0]}
                    alt={item.productId.productName}
                    className="w-full md:w-32 h-32 object-cover rounded-md md:mr-6 mb-4 md:mb-0"
                  />
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row justify-between">
                      <h4 className="text-lg font-semibold mb-2">{item.productId.productName}</h4>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Base Price: ${item?.initialPrice}</p>
                        <p className="text-md font-semibold">Item Total with tax: ${item.finalPrice}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3 mb-4">
                      <div className="text-sm px-3 py-1 bg-gray-100 rounded-full">Quantity: {item.quantity}</div>
                      <button
                        onClick={() => openAttributeModal(item.attributes)}
                        className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded-full flex items-center gap-1 hover:bg-blue-200"
                      >
                        <FaInfoCircle size={14} /> Attributes
                      </button>
                      <button
                        onClick={() => openTaxModal(item.taxes)}
                        className="text-sm px-3 py-1 bg-green-100 text-green-700 rounded-full flex items-center gap-1 hover:bg-green-200"
                      >
                        <FaInfoCircle size={14} /> Tax Details
                      </button>
                      <button
                        onClick={() => handleOpenReviewModal(item)}
                        className="text-sm px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full flex items-center gap-1 hover:bg-yellow-200"
                      >
                        <FaStar size={14} /> {hasReview ? 'Edit Review' : 'Add Review'}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 text-sm">
                      <div>
                        <p>Initial Price: ${item?.initialPrice}</p>
                        <p>Subtotal (with Attributes tax amount): ${item?.taxes[0]?.taxAmount || 0}</p>
                      </div>
                      <div>
                        {item.taxes && item.taxes.length > 0 && (
                          <p>Tax Percentage-{item?.taxes[0]?.taxPercentage}%</p>
                        )}
                        <p className="font-medium">Final Price: ${item.finalPrice}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        item={selectedItem}
        orderId={orderId}
        fetchOrderDetails={fetchOrderDetails}
      />
    </>
  );
};

export default OrderItems;